import get from 'lodash/get';
import { AppState } from '../../types/store';
import {
  MySubmissionsSortBy,
  StatsTimePeriod,
  MySubmissionItem,
  GeneralStats,
  DeviceStats,
  CountryStats,
  BenchmarkItem,
  BenchmarkSortBy,
} from '../../types/statsSection';
import { PapiUser } from '@wix/da-types-papi';
import { getCurrentUserHasCore } from '@wix/da-shared-react/pkg/publicSession';
import { getProfileOwnerUser } from './users';

export const getHasCalculatedStats = (state: AppState) =>
  state.statsSection.stats.hasCalculatedStats || false;

export const getTimePeriod = (state: AppState): StatsTimePeriod =>
  state.statsSection.timePeriod;

export const getStatsOverviewData = (state: AppState) => {
  return {
    overviewProfile: getDataForCurrentTimePeriod(
      state,
      'statsSection.stats.overviewProfile',
      []
    ),
    overviewDeviations: getDataForCurrentTimePeriod(
      state,
      'statsSection.stats.overviewDeviations',
      []
    ),
    overviewWatchers: getDataForCurrentTimePeriod(
      state,
      'statsSection.stats.overviewWatchers',
      []
    ),
    overviewFaved: getDataForCurrentTimePeriod(
      state,
      'statsSection.stats.overviewFaved',
      []
    ),
  };
};

export const currentOverviewTotals = (state: AppState, fallback) =>
  getDataForCurrentTimePeriod(
    state,
    'statsSection.stats.overviewTotal',
    fallback
  );

export const getMySubmissionsSortBy = (state: AppState): MySubmissionsSortBy =>
  state.statsSection.mySubmissionsSortBy;

export const getMySubmissionsIsLoading = (state: AppState) =>
  state.statsSection.mySubmissionsIsLoading;

export const getMySubmissionsItems = (state: AppState): MySubmissionItem[] =>
  getDataForCurrentTimePeriod(state, 'statsSection.stats.myContent', []);

export const getBenchmarkSortBy = (state: AppState): BenchmarkSortBy =>
  state.statsSection.benchmarkSortBy;

export const getBenchmarkIsLoading = (state: AppState) =>
  state.statsSection.benchmarkIsLoading;

export const getBenchmarkItems = (state: AppState): BenchmarkItem[] => {
  const profileOwner = getProfileOwnerUser(state);
  const hasCore = getCurrentUserHasCore(state);
  const data = getDataForCurrentTimePeriod(
    state,
    'statsSection.stats.compareYourself',
    []
  );
  const sortByField = state.statsSection.benchmarkSortBy;

  const sortByFunc = hasCore
    ? (a, b) => b[sortByField] - a[sortByField]
    : (a, b) => (b.user.userId === profileOwner.userId ? 1 : -1);

  const sortedData = [...data].sort(sortByFunc);

  return sortedData;
};

export const getBenchmarkUserIds = (state: AppState): number[] =>
  getBenchmarkItems(state).map(benchmarkItem => benchmarkItem.user.userId);

export const getMatchingUsers = (state: AppState): PapiUser[] => {
  const matchingUsers: PapiUser[] = get(
    state,
    'statsSection.matchingUsers',
    []
  );
  const benchmarkUserIds = getBenchmarkUserIds(state);
  return matchingUsers.filter(
    matchingUser => !benchmarkUserIds.includes(matchingUser.userId)
  );
};

export const getGeneralStats = (state: AppState): GeneralStats =>
  getDataForCurrentTimePeriod(state, 'statsSection.stats.general', {});

export const getRecentVisitors = (state: AppState): PapiUser[] =>
  getDataForCurrentTimePeriod(state, 'statsSection.stats.recentVisitors', []);

export const getWatchers = (state: AppState): PapiUser[] =>
  getDataForCurrentTimePeriod(state, 'statsSection.stats.recentWatchers', {});

export const getWatching = (state: AppState): PapiUser[] =>
  getDataForCurrentTimePeriod(state, 'statsSection.stats.recentWatching', {});

export const getDeviceStats = (state: AppState): DeviceStats =>
  getDataForCurrentTimePeriod(state, 'statsSection.stats.trafficByDevice', []);

export const getCountryStats = (state: AppState): CountryStats =>
  getDataForCurrentTimePeriod(state, 'statsSection.stats.trafficByCountry', []);

const getDataForCurrentTimePeriod = (state, key, fallback) => {
  const timePeriod = getTimePeriod(state);
  return get(state, `${key}.timePeriods[${timePeriod}]`, fallback);
};
